/* eslint-disable class-methods-use-this */
import { debounce } from 'lodash';

import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import Search from './search';
import { formatBingType } from '../utils/locationTypes';

class BingSearch extends Search {
  source() {
    const token = window.__HB_ENV__.AZURE_MAP_API_KEY;

    const geocode = debounce(async (query, asyncResults) => {
      try {
        const url = `https://atlas.microsoft.com/geocode?api-version=2025-01-01&query=${encodeURIComponent(query)}&top=5`;

        const response = await fetch(url, {
          headers: {
            'subscription-key': token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const transformedResults = data.features
          .map((feature) => {
            const { properties, geometry, bbox } = feature;
            const { address } = properties;

            // Format bounds to match expected structure
            // bbox format: [minLon, minLat, maxLon, maxLat]
            // Expected format: [[northEastLat  , northEastLng], [southWestLat, southWestLng]]
            const formattedBounds = bbox
              ? [
                  [bbox[3], bbox[2]], // [maxLat, maxLon] - northeast
                  [bbox[1], bbox[0]], // [minLat, minLon] - southwest
                ]
              : null;

            return {
              entityType: properties.type,
              address: {
                formattedAddress: address.formattedAddress,
                locality: address.locality,
                adminDistrict: address.adminDistricts?.[0]?.shortName,
                adminDistrict2: address.adminDistricts?.[1]?.shortName,
                countryRegion: address.countryRegion?.name,
                countryRegionIso2: address.countryRegion?.ISO,
                postalCode: address.postalCode,
                addressLine: address.addressLine,
                streetName: address.streetName,
                streetNumber: address.streetNumber,
              },
              geocodePoints: [
                {
                  coordinates: [
                    geometry.coordinates[1],
                    geometry.coordinates[0],
                  ], // [lat, lng]
                },
              ],
              bbox: formattedBounds,
            };
          })
          .filter(
            (result) =>
              // When selecting locations without a country, there was no reference point for distance calculations,
              // causing the system to show all users. We limit suggestions to countries and deeper locations by default.
              // However, some sensitive communities (e.g. health support groups) need anonymity and the ability to use
              // wide locations (oceans, continents etc). The anonymousLocations flag enables this broader location support.
              ['Address', 'PopulatedPlace'].includes(result.entityType) &&
              (this.anonymousLocations ||
                // eslint-disable-next-line eqeqeq
                (result.address.countryRegion != null &&
                  result.address.countryRegion.trim() !== '')),
          );

        asyncResults(transformedResults);
      } catch (error) {
        errorReporting.captureMessage(
          `Azure Maps Geocoder failed: ${error.message}`,
        );
        asyncResults([]);
      }
    }, 500);

    return (query, region, syncResults, asyncResults) => {
      syncResults([]);
      geocode(query, asyncResults);
    };
  }

  suggestionDisplay(data) {
    if (this.anonymousLocations) {
      return data.address.formattedAddress;
    }

    const type = formatBingType(data.entityType);

    return `${data.address.formattedAddress} (${type})`;
  }

  data(data) {
    return {
      city: data.address.locality || data.address.adminDistrict2,
      country: data.address.countryRegion,
      country_code: data.address.countryRegionIso2,
      location_level: null,
      lat: data.geocodePoints[0].coordinates[0],
      lng: data.geocodePoints[0].coordinates[1],
      bounds: data.bbox,
    };
  }
}

export default BingSearch;
